import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isUUID } from "validator";

import { useAuthenticatedRouteMatch } from '../../app/auths/useAuthenticatedRouteMatch';
import { SleepHeader } from "../../components/SleepHeader/SleepHeader";
import { TopLevelTransition } from '../../components/TopLevelTransition/TopLevelTransition';
import { TvattTable } from '../../components/TvattTable/TvattTable';
import { SET_NOTIFICATIONS, useMainDispatch, useMainState } from '../../contexts/MainContext';
import { usePrimusState } from "../../contexts/PrimusContext";
import {
  SET_INDEXED_DATA,
  SET_TABLE_DATA,
  SET_TABLE_USAGE_DATA,
  useTableDispatch,
  useTableState
} from "../../contexts/TableContext";
import { getDefaultColumns, getTermsColumns } from '../../declarations/TvattTableColumnDeclarations';
import { dumpAllIndexedData, toggleBookmarkedRow, toggleEditedRow } from '../../services/IndexedDbService';
import { PrimusApi } from '../../services/PrimusApi';
import { createSetInvalidOperation, createSetValidOperation } from '../../utils/primusMetaOperationHandler';

export const DuplicateView = () => {
  const mainDispatch = useMainDispatch();
  const tableDispatch = useTableDispatch();
  const mainDispatchCallback = useCallback(mainDispatch, [mainDispatch]);
  const tableDispatchCallback = useCallback(tableDispatch, [tableDispatch]);

  const match = useAuthenticatedRouteMatch('/duplicate');

  const [tableLoading, setTableLoading] = useState(false);
  const [usagesLoading, setUsagesLoading] = useState(false);

  const {notifications, refreshTable } = useMainState();
  const { data, indexedData, refreshUsage } = useTableState();
  const { baseUrl, selectedList, selectedListStatus, versionMajor, versionMinor } = usePrimusState();
  const { t } = useTranslation('translations');

  const api = useMemo(() => new PrimusApi(baseUrl), [baseUrl]);

  const dispatchSetTableData = (data, refreshUsage) => {
    tableDispatchCallback({
      type: SET_TABLE_DATA,
      data: [...data],
      refreshUsage
    })
  };

  const bookmarkRow = useCallback((row) => {
    let tempTableData = data;
    const index = tempTableData.findIndex((r) => r.id === row.id);

    if(index !== -1) {
      tempTableData[index].bookmarked = !row.original.bookmarked;
      toggleBookmarkedRow(row.id, tempTableData[index].bookmarked).then(() => {
        return dumpAllIndexedData();
      }).then((data) => {
        tableDispatchCallback({
          type: SET_INDEXED_DATA,
          indexedData: data
        });
      });
    }

    dispatchSetTableData(tempTableData);
  }, [data, tableDispatchCallback]);

  const toggleStatus = useCallback((row, checked) => {
    let tempTableData = data;
    let index = tempTableData.findIndex((r) => row.id === r.id);

    if (index !== -1) {
      tempTableData[index].isLoading = true;
      dispatchSetTableData(tempTableData);

      let operation = checked
          ? createSetInvalidOperation([row], -1)
          : createSetValidOperation([row], -1);

      return api.executeOperationStep(operation).then(() => {
        tempTableData[index].isLoading = false;
        tempTableData[index].inValid = checked;
        tempTableData[index].edit = new Date().toISOString();
      }).finally(() => {
        toggleEditedRow(tempTableData[index]).then(dumpAllIndexedData)
          .then((data) => {
            tableDispatchCallback({
              type: SET_INDEXED_DATA,
              indexedData: data
            });
          });
        dispatchSetTableData(tempTableData);
      });
    }
  }, [mainDispatchCallback, data, notifications]);

  const columns = useMemo(() => {
    if (isUUID(selectedList.split('-').splice(1).join('-')) || selectedList.startsWith('(')) {
      if (selectedList.startsWith('ct_25')) {
        return getTermsColumns(t, api, bookmarkRow, toggleStatus);
      }
      return getDefaultColumns(t, api, toggleStatus, bookmarkRow);
    }
    else {
      return getTermsColumns(t, api, bookmarkRow, toggleStatus);
    }
  }, [t, api, bookmarkRow, toggleStatus]);

  useEffect(() => {
    if (!!selectedList && match) {
      setTableLoading(true);

      api.getSelectedListData(indexedData, selectedList, t, true, selectedListStatus).then((list) => {
        dispatchSetTableData(list, true);
        setTableLoading(false);
        return list;
      }).catch((e) => {
        mainDispatchCallback({
          type: SET_NOTIFICATIONS,
          notifications: [...notifications,
            {
              text: t('table.default.jobs.errorSettingTableData'),
              type: 'error',
              error: e.message || e,
            }
          ]
        });
      });
    }
  }, [refreshTable, selectedListStatus, baseUrl, selectedList, match])

  useEffect(() => {
    if (match && !!refreshUsage && data.length && (versionMajor > 0 || versionMinor > 222)) {
      if (data.length > 10000) {
        setUsagesLoading(true);
      }
      api.getTableUsage(data).then((uData) =>
        tableDispatchCallback({
          type: SET_TABLE_USAGE_DATA,
          usageData: uData
        })
      ).finally(() => setUsagesLoading(false));
    }
  }, [refreshUsage])

  if (match) {
    return (
      <TopLevelTransition>
        <SleepHeader />
        <TvattTable
          columns={columns}
          groupBy={['caption']}
          isLoading={tableLoading}
          sortBy={[{ id: 'duplicates', desc: true }]}
          muiTableBodyCellProps={({ cell, row }) => {
            if (row.depth > 0 && cell.column.getGroupedIndex() === 0) {
              return {
                sx: { paddingLeft: '24px' },
                children: row.original.caption
              };
            }
            return cell;
          }}
          usagesLoading={usagesLoading}
        />
      </TopLevelTransition>
    )
  }
}